import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule, MatChipInputEvent } from '@angular/material/chips';
import { RmsModule } from '@rms'
import { EmailComposerOptions, EmailComposerResult, EmailAttachment } from '@interfaces';
import { EmailTemplateService, RmsMessageService } from '@services';

@Component({
  selector: 'app-email-composer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    RmsModule
  ],
  templateUrl: './email-composer.component.html',
  styleUrls: ['./email-composer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EmailComposerComponent {
  @Input() open = false;
  @Output() closed = new EventEmitter<EmailComposerResult>();

  
  private loadedTemplate?: string;
  private isRebuilding = false;

  attachments: EmailAttachment[] = [];
  options!: EmailComposerOptions;
  form: any;
  // store arrays in form controls


  // used by chip inputs
  readonly separatorKeys = [13, 188]; // Enter, Comma (optional)

  constructor(private fb: FormBuilder, private templates: EmailTemplateService, private message: RmsMessageService) {
    this.form = this.fb.group({
      from: this.fb.nonNullable.control('', Validators.required),
      to: this.fb.nonNullable.control<string[]>([], Validators.required),
      cc: this.fb.nonNullable.control<string[]>([]),
      bcc: this.fb.nonNullable.control<string[]>([]),
      subject: this.fb.nonNullable.control('', Validators.required),

      // NEW
      salutation: this.fb.nonNullable.control('Dear', Validators.required),
      dearName: this.fb.nonNullable.control('', Validators.required), 
      optionalText: this.fb.control<string | null>(null),

      html: this.fb.nonNullable.control('', Validators.required),
      text: this.fb.control<string | null>(null),
    });

  }

  async openComposer(opts: EmailComposerOptions) {
    this.options = opts;
    this.open = true;

    // Defaults
    this.form.patchValue({
      from: opts.defaultFrom ?? (opts.fromOptions?.[0] ?? ''),
      to: opts.to ?? [],
      cc: opts.cc ?? [],
      bcc: opts.bcc ?? [],
      subject: opts.subject ?? '',

      // NEW defaults
      salutation: (opts as any).salutation ?? 'Dear',
      dearName: (opts as any).dearName ?? '',
      optionalText: (opts as any).optionalText ?? null,

      html: opts.initialHtml ?? '',
      text: opts.initialText ?? null
    });

    // Load template if provided

    if (opts.templatePath) {
      this.loadedTemplate = await this.templates.load(opts.templatePath);

      // Build initial html once
      this.rebuildHtmlFromTemplate();

      // Then keep preview live as user edits fields
      this.setupLivePreview();
    } else {
      this.loadedTemplate = undefined;
    }

    
    this.attachments = (this.options.attachments || []).map(a => ({ ...a }));
    // Optional validation toggles
    if (!opts.requireSubject) this.form.get('subject')?.clearValidators();
    if (!opts.requireTo) this.form.get('to')?.clearValidators(); // (see note below)
    this.form.get('subject')?.updateValueAndValidity();
    this.form.get('to')?.updateValueAndValidity();
  }
  private escapeHtml(s: string) {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  addChip(controlName: 'to' | 'cc' | 'bcc', event: MatChipInputEvent) {
    const value = (event.value || '').trim();
    if (!value) return;

    const ctrl = this.form.get(controlName) as FormControl<string[]>;
    const next = [...(ctrl.value ?? [])];

    // basic de-dupe
    if (!next.includes(value)) next.push(value);

    ctrl.setValue(next);
    ctrl.markAsDirty();
    event.chipInput?.clear();
  }

  removeChip(controlName: 'to' | 'cc' | 'bcc', email: string) {
    const ctrl = this.form.get(controlName) as FormControl<string[]>;
    ctrl.setValue((ctrl.value ?? []).filter(x => x !== email));
    ctrl.markAsDirty();
  }

  cancel() {
    this.open = false;
    this.closed.emit({ cancelled: true });
  }
  submit() {
    if (this.form.invalid) { /* ... */ return; }

    const v = this.form.getRawValue();

    // If user didn't load a template, inject global pieces above the body
    let finalHtml = v.html;

    if (!this.options?.templatePath) {
      const optionalParagraph =
        v.optionalText && v.optionalText.trim()
          ? `<p style="margin:16px 0 0;">${this.escapeHtml(v.optionalText).replace(/\n/g, '<br/>')}</p>`
          : '';

      const salutationLine =
        (v.salutation || 'Dear') && (v.dearName || '')
          ? `<p style="margin-top:0;">${this.escapeHtml(v.salutation || 'Dear')} <strong>${this.escapeHtml(v.dearName || '')}</strong>,</p>`
          : '';

      finalHtml = `${salutationLine}${optionalParagraph}${finalHtml}`;
    }

    this.closed.emit({
      cancelled: false,
      payload: {
        from: v.from,
        to: v.to ?? [],
        cc: v.cc ?? [],
        bcc: v.bcc ?? [],
        subject: v.subject,
        html: finalHtml,
        text: v.text ?? undefined,
        attachments: this.attachments.length ? this.attachments : undefined
      }
    });
  }

  async onPickFiles(evt: Event) {
    const input = evt.target as HTMLInputElement;
    if (!input.files?.length) return;

    for (const file of Array.from(input.files)) {
      const base64 = await this.readFileAsBase64(file);
      this.attachments.push({
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64: base64
      });
    }

    input.value = '';
  }

  removeAttachment(i: number) {
    this.attachments.splice(i, 1);
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const s = String(reader.result || '');
        // result is "data:...;base64,XXXX"
        const b64 = s.includes(',') ? s.split(',')[1] : s;
        resolve(b64);
      };
      reader.readAsDataURL(file);
    });
  }
  private rebuildHtmlFromTemplate() {
    if (!this.loadedTemplate || this.isRebuilding) return;

    const v = this.form.getRawValue();

    const optionalParagraph =
      v.optionalText && v.optionalText.trim()
        ? `<p style="margin:16px 0 0;">${this.escapeHtml(v.optionalText).replace(/\n/g, '<br/>')}</p>`
        : '';

    const merged = {
      ...(this.options?.templateReplacements ?? {}),
      '@@Salutation@@': v.salutation || 'Dear',
      '@@DearName@@': v.dearName || '',
      '@@OptionalParagraph@@': optionalParagraph
    };

    const nextHtml = this.templates.applyReplacements(this.loadedTemplate, merged);

    this.isRebuilding = true;
    this.form.patchValue({ html: nextHtml }, { emitEvent: false }); // IMPORTANT: no emitEvent loop
    this.isRebuilding = false;
  }
  private setupLivePreview() {
    const sal = this.form.get('salutation');
    const name = this.form.get('dearName');
    const opt = this.form.get('optionalText');

    // re-render whenever any of these change
    sal?.valueChanges.subscribe(() => this.rebuildHtmlFromTemplate());
    name?.valueChanges.subscribe(() => this.rebuildHtmlFromTemplate());
    opt?.valueChanges.subscribe(() => this.rebuildHtmlFromTemplate());
  }
}
